// The rubric editor: what the AI interview assesses, and what a good answer is.
//
// WHY THIS SCREEN EXISTS
// The backend has had rubric CRUD, versioning and validation since the module
// was built, and `interviewService.saveRubric` has been sitting in the service
// file unused the whole time. Nothing called it. Every rubric that ever existed
// came from a seed script, so in practice every job ran on a generic starter
// draft that nobody could see, let alone change — and those anchors decide what
// a candidate's 1-to-5 actually means.
//
// WHAT A RUBRIC CONTROLS, SO THE STAKES ARE CLEAR ON SCREEN
//   competencies  -> become the interview questions (Q2-Q5), and their weights
//                    are the whole score
//   anchors       -> what a 1, a 3 and a 5 sound like. The model scores against
//                    these verbatim, so vague anchors produce indefensible scores
//   hard gates    -> asked verbatim, evaluated in plain code, can REJECT
//
// SAVING CREATES A NEW VERSION, AND THAT IS SURFACED
// Scores are only comparable against the anchors that produced them, so an edit
// never mutates in place — it writes version + 1 and leaves already-scored
// interviews on the old one. Somebody about to change an anchor after twelve
// people have interviewed needs to know that before they press save, not after,
// which is why the version panel shows interview counts per version.
//
// VALIDATION IS THE SERVER'S, MIRRORED — NOT REIMPLEMENTED
// The server returns every problem at once (422 with an `errors` array) because
// fixing one error per save is a miserable way to author four competencies. The
// live weight total here is the one rule checked as you type, because it is the
// one you cannot hold in your head while editing four numbers.

import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Chip,
  CircularProgress,
  Collapse,
  Divider,
  IconButton,
  MenuItem,
  Snackbar,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import ArrowBackRoundedIcon from "@mui/icons-material/ArrowBackRounded";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import DeleteOutlineRoundedIcon from "@mui/icons-material/DeleteOutlineRounded";
import HistoryRoundedIcon from "@mui/icons-material/HistoryRounded";
import SaveRoundedIcon from "@mui/icons-material/SaveRounded";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import WarningAmberRoundedIcon from "@mui/icons-material/WarningAmberRounded";
import interviewService from "../services/interviewService";

const FONT = "'Plus Jakarta Sans', sans-serif";

const C = {
  ink: "#18181b",
  body: "#3f3f46",
  muted: "#71717a",
  faint: "#a1a1aa",
  hair: "#f4f4f5",
  line: "#e4e4e7",
  wash: "#fafafa",
  card: "#ffffff",
  accent: "#6366f1",
  accentSoft: "#eef2ff",
};

// Mirrors GATE_OPERATORS in utils/hiringRubricSchema. Deliberately small: every
// one is decidable from a short answer without judgement, which is what keeps
// the REJECTED path explainable. Anything needing interpretation is a
// competency, not a gate.
const GATE_OPS = [
  { key: "required", label: "Must answer yes", hint: "Rejected if the answer is not affirmative." },
  { key: "min", label: "At least", hint: "Numeric answer must be at least this." },
  { key: "max", label: "At most", hint: "Numeric answer must be at most this." },
];

const blankCompetency = () => ({
  key: "",
  label: "",
  weight: 0,
  anchors: { 1: "", 3: "", 5: "" },
});

const blankGate = () => ({ key: "", question: "", required: true, unit: "" });

// Which operator a gate is currently using. The server requires exactly one of
// them to be present, so this reads whichever it is rather than storing a
// separate "type" the payload would then have to strip.
const gateOp = (g) => (("min" in g) ? "min" : ("max" in g) ? "max" : "required");

const Label = ({ children, hint }) => (
  <Stack direction="row" spacing={0.75} alignItems="center" sx={{ mb: 0.75 }}>
    <Typography
      sx={{
        fontFamily: FONT,
        fontSize: 11,
        fontWeight: 750,
        letterSpacing: 0.5,
        textTransform: "uppercase",
        color: C.faint,
      }}
    >
      {children}
    </Typography>
    {hint && (
      <Tooltip title={hint}>
        <Box
          sx={{
            width: 13,
            height: 13,
            borderRadius: "50%",
            display: "grid",
            placeItems: "center",
            border: `1px solid ${C.line}`,
            fontSize: 9,
            color: C.faint,
            cursor: "help",
          }}
        >
          ?
        </Box>
      </Tooltip>
    )}
  </Stack>
);

const field = {
  "& .MuiOutlinedInput-root": {
    borderRadius: "8px",
    bgcolor: C.card,
    fontFamily: FONT,
    fontSize: 13.5,
  },
  "& fieldset": { borderColor: C.line },
};

const RubricEditor = () => {
  const { stage_id } = useParams();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [toast, setToast] = useState(null);

  const [rubric, setRubric] = useState(null);
  // True when the server handed back a starter draft rather than a saved row —
  // version 0, never persisted. Distinct from `needsReview`, which is a SAVED
  // rubric that no human has authored.
  const [isDraft, setIsDraft] = useState(false);
  const [serverErrors, setServerErrors] = useState([]);

  const [versions, setVersions] = useState(null);
  const [showVersions, setShowVersions] = useState(false);

  // ── Questions ─────────────────────────────────────────────────────────────
  //
  // Kept separate from `rubric` because they are a separate record with
  // separate rules: the rubric versions on save, questions replace, and
  // questions lock once anybody has interviewed on them.
  const [questions, setQuestions] = useState([]);
  const [interviewedCount, setInterviewedCount] = useState(0);
  const [drafting, setDrafting] = useState(false);
  const [savingQ, setSavingQ] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await interviewService.getRubric(stage_id);
      // 200 with statusCode in the body, so a failure never reaches a catch.
      if (!res.data?.success) {
        setError(res.data?.statusMessage || "Could not load this rubric.");
        return;
      }
      const r = res.data.data.rubric;
      setIsDraft(Boolean(res.data.data.is_draft));
      setRubric({
        ...r,
        competencies: (r.competencies || []).map((c) => ({
          ...c,
          // Normalised to string keys so the three inputs bind consistently —
          // the server sends {1,3,5} and JSON gives them back as strings.
          anchors: { 1: c.anchors?.[1] ?? "", 3: c.anchors?.[3] ?? "", 5: c.anchors?.[5] ?? "" },
        })),
        hard_gates: r.hard_gates || [],
        total_questions: r.total_questions ?? 12,
        duration_minutes: r.duration_minutes ?? 30,
      });
    } catch (err) {
      setError(err.response?.data?.statusMessage || err.message);
    } finally {
      setLoading(false);
    }
  }, [stage_id]);

  useEffect(() => {
    load();
  }, [load]);

  // Loaded by JOB, which only becomes known once the rubric has loaded — the
  // route carries a stage id and questions hang off the job.
  const jobId = rubric?.job_id;
  useEffect(() => {
    if (!jobId) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await interviewService.authoredQuestions(jobId);
        if (cancelled || !res.data?.success) return;
        setQuestions(res.data.data.questions || []);
        setInterviewedCount(res.data.data.interviewed || 0);
      } catch {
        // A questions list that failed to load is not worth blocking the rubric
        // editor over; the section shows its empty state.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [jobId]);

  // Locked once anyone has interviewed. The questions are what the recorded
  // answers are answers TO, so editing them afterwards leaves a reviewer
  // reading the wrong question above the right answer.
  const qLocked = interviewedCount > 0;

  // Competencies that no question asks about. They still carry their weight, so
  // that slice of the score would rest on whatever the candidate volunteers.
  const uncovered = useMemo(() => {
    const asked = new Set(questions.map((q) => q.competency_key).filter(Boolean));
    return (rubric?.competencies || []).filter(
      (c) => Number(c.weight) > 0 && !asked.has(c.key),
    );
  }, [questions, rubric]);

  const draftQuestions = async () => {
    setDrafting(true);
    try {
      const res = await interviewService.draftQuestions(jobId, 6);
      if (!res.data?.success) {
        setToast({ severity: "error", message: res.data?.statusMessage });
        return;
      }
      // Replaces what is on screen but saves nothing — the save button is a
      // separate, deliberate act.
      setQuestions(res.data.data.questions || []);
      setToast({
        severity: "info",
        message: "Drafted from the job description. Read them, edit anything that is off, then save.",
      });
    } catch (err) {
      setToast({
        severity: "error",
        message: err.response?.data?.statusMessage || err.message,
      });
    } finally {
      setDrafting(false);
    }
  };

  const saveQuestions = async () => {
    setSavingQ(true);
    try {
      const res = await interviewService.saveQuestions(jobId, questions);
      if (!res.data?.success) {
        setToast({ severity: "error", message: res.data?.statusMessage });
        return;
      }
      setToast({ severity: "success", message: res.data.statusMessage });
    } catch (err) {
      setToast({
        severity: "error",
        message: err.response?.data?.statusMessage || err.message,
      });
    } finally {
      setSavingQ(false);
    }
  };

  const loadVersions = useCallback(async () => {
    try {
      const res = await interviewService.rubricVersions(stage_id);
      if (res.data?.success) setVersions(res.data.data.versions || []);
    } catch {
      // A missing history panel is not worth an error banner over the editor.
      setVersions([]);
    }
  }, [stage_id]);

  useEffect(() => {
    if (showVersions && versions === null) loadVersions();
  }, [showVersions, versions, loadVersions]);

  // The one rule checked as you type. Four weights that must total exactly 100
  // is not something anybody holds in their head while editing them, and
  // finding out on save costs a round trip and the scroll position.
  const weightTotal = useMemo(
    () => (rubric?.competencies || []).reduce((s, c) => s + (Number(c.weight) || 0), 0),
    [rubric],
  );
  const weightsOk = Math.abs(weightTotal - 100) < 0.01;

  const patchComp = (i, patch) =>
    setRubric((r) => ({
      ...r,
      competencies: r.competencies.map((c, n) => (n === i ? { ...c, ...patch } : c)),
    }));

  const patchGate = (i, patch) =>
    setRubric((r) => ({
      ...r,
      hard_gates: r.hard_gates.map((g, n) => (n === i ? { ...g, ...patch } : g)),
    }));

  // Switching operator REPLACES rather than adds. The server requires exactly
  // one of required/min/max to be present, so leaving the old key behind is a
  // 422 the author cannot see the cause of.
  const setGateOp = (i, op) =>
    setRubric((r) => ({
      ...r,
      hard_gates: r.hard_gates.map((g, n) => {
        if (n !== i) return g;
        const { required, min, max, ...rest } = g;
        if (op === "required") return { ...rest, required: true };
        if (op === "min") return { ...rest, min: Number(min ?? max ?? 0) };
        return { ...rest, max: Number(max ?? min ?? 0) };
      }),
    }));

  const save = async () => {
    setSaving(true);
    setServerErrors([]);
    try {
      const payload = {
        competencies: rubric.competencies.map((c) => ({
          key: String(c.key || "").trim(),
          label: String(c.label || "").trim(),
          weight: Number(c.weight) || 0,
          anchors: { 1: c.anchors[1], 3: c.anchors[3], 5: c.anchors[5] },
        })),
        hard_gates: rubric.hard_gates.map((g) => {
          const op = gateOp(g);
          return {
            key: String(g.key || "").trim(),
            question: String(g.question || "").trim(),
            ...(g.unit ? { unit: g.unit } : {}),
            ...(op === "required" ? { required: true } : { [op]: Number(g[op]) || 0 }),
          };
        }),
        total_questions: Number(rubric.total_questions),
        duration_minutes: Number(rubric.duration_minutes),
      };

      const res = await interviewService.saveRubric(stage_id, payload);
      const body = res.data;
      if (!body?.success) {
        // Every problem at once, which is why the server sends an array.
        setServerErrors(body?.errors || []);
        setToast({ severity: "error", message: body?.statusMessage || "Could not save." });
        return;
      }
      setToast({ severity: "success", message: body.statusMessage });
      // Refetched rather than patched from the response: saving creates a NEW
      // version, and the editor should be sitting on the row that is now active.
      setVersions(null);
      await load();
    } catch (err) {
      const body = err.response?.data;
      setServerErrors(body?.errors || []);
      setToast({ severity: "error", message: body?.statusMessage || err.message });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: "grid", placeItems: "center", py: 12 }}>
        <CircularProgress size={26} sx={{ color: C.line }} />
      </Box>
    );
  }

  if (error || !rubric) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert
          severity="error"
          sx={{ borderRadius: 2 }}
          action={
            <Button size="small" onClick={() => navigate(-1)} sx={{ textTransform: "none" }}>
              Go back
            </Button>
          }
        >
          {error || "This rubric could not be loaded."}
        </Alert>
      </Box>
    );
  }

  // A saved rubric with no author: written by the system when the job was
  // created, never read by a human. Different from isDraft, which was never
  // saved at all.
  const needsReview = !isDraft && rubric.created_by == null;

  return (
    <Box sx={{ p: { xs: 1.5, md: 2 }, fontFamily: FONT, maxWidth: 1100 }}>
      <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 0.5 }}>
        <IconButton size="small" onClick={() => navigate(-1)}>
          <ArrowBackRoundedIcon sx={{ fontSize: 20, color: C.muted }} />
        </IconButton>
        <Typography sx={{ fontFamily: FONT, fontWeight: 800, fontSize: 21, color: C.ink }}>
          Interview rubric
        </Typography>
        {rubric.version > 0 && (
          <Chip
            size="small"
            label={`v${rubric.version}`}
            sx={{ height: 20, fontSize: 11, fontWeight: 700, bgcolor: C.hair, color: C.muted }}
          />
        )}
        <Box sx={{ flex: 1 }} />
        <Button
          startIcon={<HistoryRoundedIcon sx={{ fontSize: 17 }} />}
          onClick={() => setShowVersions((v) => !v)}
          sx={{ textTransform: "none", fontWeight: 650, fontSize: 13, color: C.muted }}
        >
          History
        </Button>
        <Button
          variant="contained"
          disableElevation
          startIcon={
            saving ? <CircularProgress size={15} sx={{ color: "#fff" }} /> : <SaveRoundedIcon sx={{ fontSize: 17 }} />
          }
          disabled={saving}
          onClick={save}
          sx={{
            textTransform: "none",
            fontWeight: 700,
            fontSize: 13.5,
            borderRadius: "8px",
            px: 2,
            bgcolor: C.ink,
            "&:hover": { bgcolor: "#27272a" },
          }}
        >
          {saving ? "Saving…" : rubric.version > 0 ? `Save as v${rubric.version + 1}` : "Save rubric"}
        </Button>
      </Stack>

      <Typography sx={{ fontFamily: FONT, fontSize: 13, color: C.muted, mb: 2, ml: 5.5 }}>
        These competencies become the interview questions, and the anchors are
        what the AI scores each answer against. Saving never overwrites — it
        creates a new version, so anyone already interviewed keeps the anchors
        they were marked with.
      </Typography>

      {(isDraft || needsReview) && (
        <Alert
          severity="warning"
          icon={<WarningAmberRoundedIcon />}
          sx={{ mb: 2, borderRadius: 2, fontFamily: FONT, fontSize: 13.5 }}
        >
          <AlertTitle sx={{ fontFamily: FONT, fontWeight: 750, fontSize: 14 }}>
            {isDraft ? "Not saved yet" : "Nobody has reviewed this rubric"}
          </AlertTitle>
          {isDraft
            ? "This is a generic starter draft. Nothing is stored for this round until you save."
            : "This was generated automatically when the job was created. The anchors are generic — they were written for “an operational role”, not for this one — and every interview scored against them inherits that. Edit them and save before interviewing anybody."}
        </Alert>
      )}

      <Collapse in={showVersions}>
        <Box sx={{ mb: 2, p: 1.5, borderRadius: 2, border: `1px solid ${C.line}`, bgcolor: C.wash }}>
          <Typography sx={{ fontFamily: FONT, fontSize: 12, fontWeight: 750, color: C.muted, mb: 1 }}>
            VERSION HISTORY
          </Typography>
          {versions === null ? (
            <CircularProgress size={16} sx={{ color: C.line }} />
          ) : versions.length === 0 ? (
            <Typography sx={{ fontFamily: FONT, fontSize: 13, color: C.faint }}>
              Nothing saved yet.
            </Typography>
          ) : (
            versions.map((v) => (
              <Stack
                key={v.id}
                direction="row"
                spacing={1.5}
                alignItems="center"
                sx={{ py: 0.6, borderTop: `1px solid ${C.hair}` }}
              >
                <Typography sx={{ fontFamily: FONT, fontSize: 13, fontWeight: 700, width: 40 }}>
                  v{v.version}
                </Typography>
                {v.is_active && (
                  <Chip
                    size="small"
                    label="active"
                    sx={{ height: 18, fontSize: 10, fontWeight: 700, bgcolor: "#dcfce7", color: "#166534" }}
                  />
                )}
                <Typography sx={{ fontFamily: FONT, fontSize: 12.5, color: C.muted, flex: 1 }}>
                  {/* The number that decides whether changing an anchor is safe. */}
                  {v.interviews_scored} interview{v.interviews_scored === 1 ? "" : "s"} scored against it
                </Typography>
                <Typography sx={{ fontFamily: FONT, fontSize: 12, color: C.faint }}>
                  {v.created_by == null ? "auto-generated" : `by user ${v.created_by}`}
                </Typography>
              </Stack>
            ))
          )}
        </Box>
      </Collapse>

      {serverErrors.length > 0 && (
        <Alert severity="error" sx={{ mb: 2, borderRadius: 2, fontFamily: FONT, fontSize: 13 }}>
          <AlertTitle sx={{ fontFamily: FONT, fontWeight: 750, fontSize: 14 }}>
            Fix these before saving
          </AlertTitle>
          {serverErrors.map((e, i) => (
            <Typography key={i} sx={{ fontFamily: FONT, fontSize: 13, lineHeight: 1.7 }}>
              • {e}
            </Typography>
          ))}
        </Alert>
      )}

      {/* ── Competencies ───────────────────────────────────────────────── */}
      <Stack direction="row" alignItems="center" sx={{ mb: 1 }}>
        <Typography sx={{ fontFamily: FONT, fontWeight: 750, fontSize: 16, color: C.ink }}>
          Competencies
        </Typography>
        <Typography sx={{ fontFamily: FONT, fontSize: 12.5, color: C.faint, ml: 1.5 }}>
          {/* The cap is not arbitrary: Q2-Q5 are the competency slots, so a
              fifth would never be asked about while still carrying weight. */}
          up to 4 — one interview question each
        </Typography>
        <Box sx={{ flex: 1 }} />
        <Chip
          size="small"
          label={`weights total ${weightTotal}${weightsOk ? "" : " — must be 100"}`}
          sx={{
            height: 22,
            fontSize: 11.5,
            fontWeight: 700,
            bgcolor: weightsOk ? "#dcfce7" : "#fef2f2",
            color: weightsOk ? "#166534" : "#b91c1c",
          }}
        />
      </Stack>

      {rubric.competencies.map((c, i) => (
        <Box
          key={i}
          sx={{ mb: 1.25, p: 1.75, borderRadius: 2, border: `1px solid ${C.line}`, bgcolor: C.card }}
        >
          <Stack direction={{ xs: "column", sm: "row" }} spacing={1.25} sx={{ mb: 1.5 }}>
            <Box sx={{ flex: 2, minWidth: 0 }}>
              <Label>What you are assessing</Label>
              <TextField
                fullWidth
                size="small"
                value={c.label}
                onChange={(e) => patchComp(i, { label: e.target.value })}
                placeholder="e.g. Hands-on ERPNext customisation"
                sx={field}
              />
            </Box>
            <Box sx={{ width: { xs: "100%", sm: 190 } }}>
              <Label hint="Lowercase letters, numbers and underscores. Identifies this competency in scores and reports, so changing it later breaks the link to past interviews.">
                Key
              </Label>
              <TextField
                fullWidth
                size="small"
                value={c.key}
                onChange={(e) =>
                  patchComp(i, { key: e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, "_") })
                }
                placeholder="erpnext_depth"
                sx={field}
              />
            </Box>
            <Box sx={{ width: { xs: "100%", sm: 110 } }}>
              <Label hint="Share of the total score. All competencies must add up to 100.">
                Weight
              </Label>
              <TextField
                fullWidth
                size="small"
                value={c.weight}
                onChange={(e) => patchComp(i, { weight: e.target.value.replace(/[^0-9.]/g, "") })}
                inputProps={{ inputMode: "numeric" }}
                sx={field}
              />
            </Box>
            <Box sx={{ display: "grid", placeItems: "center", pt: { sm: 2.5 } }}>
              <Tooltip title="Remove this competency">
                <IconButton
                  size="small"
                  onClick={() =>
                    setRubric((r) => ({
                      ...r,
                      competencies: r.competencies.filter((_, n) => n !== i),
                    }))
                  }
                >
                  <DeleteOutlineRoundedIcon sx={{ fontSize: 18, color: C.faint }} />
                </IconButton>
              </Tooltip>
            </Box>
          </Stack>

          <Label hint="The model scores each answer by matching it to whichever of these it most resembles. Vague anchors produce numbers nobody can defend.">
            What a 1, a 3 and a 5 actually sound like
          </Label>
          <Stack spacing={0.75}>
            {["1", "3", "5"].map((level) => (
              <Stack key={level} direction="row" spacing={1} alignItems="flex-start">
                <Box
                  sx={{
                    width: 26,
                    height: 30,
                    flexShrink: 0,
                    display: "grid",
                    placeItems: "center",
                    borderRadius: "6px",
                    bgcolor: level === "5" ? "#dcfce7" : level === "1" ? "#fef2f2" : C.hair,
                    color: level === "5" ? "#166534" : level === "1" ? "#b91c1c" : C.muted,
                    fontFamily: FONT,
                    fontWeight: 800,
                    fontSize: 12.5,
                  }}
                >
                  {level}
                </Box>
                <TextField
                  fullWidth
                  size="small"
                  multiline
                  maxRows={3}
                  value={c.anchors[level]}
                  onChange={(e) =>
                    patchComp(i, { anchors: { ...c.anchors, [level]: e.target.value } })
                  }
                  placeholder={
                    level === "5"
                      ? "Concrete examples with numbers, tools and outcomes, unprompted."
                      : level === "3"
                        ? "Describes it in general terms, with specifics on request."
                        : "Cannot describe it; answers stay abstract or off-topic."
                  }
                  sx={field}
                />
              </Stack>
            ))}
          </Stack>
        </Box>
      ))}

      {rubric.competencies.length < 4 && (
        <Button
          startIcon={<AddRoundedIcon sx={{ fontSize: 18 }} />}
          onClick={() =>
            setRubric((r) => ({ ...r, competencies: [...r.competencies, blankCompetency()] }))
          }
          sx={{ textTransform: "none", fontWeight: 650, fontSize: 13, color: C.accent, mb: 3 }}
        >
          Add competency
        </Button>
      )}

      <Divider sx={{ my: 2, borderColor: C.hair }} />

      {/* ── The questions ────────────────────────────────────────────────
          Its own save, not the page's.

          Questions and the rubric are two different records with two different
          rules: saving the rubric writes a new version, saving questions
          replaces the list and is refused once anyone has interviewed. One
          button doing both would have to explain that, and would half-fail in a
          way neither message could describe. */}
      <Stack direction="row" alignItems="center" sx={{ mb: 0.5, flexWrap: "wrap", rowGap: 1 }}>
        <Typography sx={{ fontFamily: FONT, fontWeight: 750, fontSize: 16, color: C.ink }}>
          Interview questions
        </Typography>
        <Typography sx={{ fontFamily: FONT, fontSize: 12.5, color: C.faint, ml: 1.5 }}>
          up to 9 — asked word for word
        </Typography>
        <Box sx={{ flex: 1 }} />
        <Button
          startIcon={
            drafting ? <CircularProgress size={14} sx={{ color: C.accent }} /> : <AutoAwesomeIcon sx={{ fontSize: 16 }} />
          }
          disabled={drafting || qLocked}
          onClick={draftQuestions}
          sx={{ textTransform: "none", fontWeight: 650, fontSize: 13, color: C.accent }}
        >
          {drafting ? "Writing…" : questions.length ? "Redraft from job description" : "Draft from job description"}
        </Button>
        <Button
          variant="outlined"
          disableElevation
          disabled={savingQ || qLocked}
          onClick={saveQuestions}
          sx={{
            ml: 1,
            textTransform: "none",
            fontWeight: 700,
            fontSize: 13,
            borderRadius: "8px",
            color: C.ink,
            borderColor: C.line,
          }}
        >
          {savingQ ? "Saving…" : "Save questions"}
        </Button>
      </Stack>

      <Typography sx={{ fontFamily: FONT, fontSize: 12.5, color: C.muted, mb: 1.5, maxWidth: 820 }}>
        Every candidate for this job is asked these, in this order, word for word
        — that sameness is the only thing that makes two candidates' scores
        comparable. On top of these the interview adds a warm-up, a few questions
        drawn from each candidate’s own CV, and the hard gates below.
      </Typography>

      {qLocked && (
        <Alert severity="info" sx={{ mb: 1.5, borderRadius: 2, fontFamily: FONT, fontSize: 13 }}>
          {interviewedCount} candidate{interviewedCount === 1 ? " has" : "s have"} already
          interviewed on these questions, so they are locked. Editing them now would
          leave recorded answers sitting under questions nobody was asked.
        </Alert>
      )}

      {/* Which competencies nothing asks about.
          A competency carries a weight and the weights are the score, so one
          with no question is a slice of the score resting on whatever the
          candidate happens to mention. Worth catching before the interview, not
          after reading a transcript and wondering where the number came from. */}
      {!qLocked && questions.length > 0 && uncovered.length > 0 && (
        <Alert severity="warning" sx={{ mb: 1.5, borderRadius: 2, fontFamily: FONT, fontSize: 13 }}>
          Nothing asks about{" "}
          <strong>{uncovered.map((c) => `${c.label} (${c.weight}%)`).join(", ")}</strong>.
          Those competencies still carry their weight in the score, so add a
          question or drop the weight to zero.
        </Alert>
      )}

      {questions.length === 0 && !drafting && (
        <Box
          sx={{
            py: 4,
            px: 2,
            mb: 1.5,
            borderRadius: 2,
            border: `1px dashed ${C.line}`,
            bgcolor: C.wash,
            textAlign: "center",
          }}
        >
          <Typography sx={{ fontFamily: FONT, fontSize: 13.5, color: C.muted }}>
            No questions yet — the interview will only ask a warm-up, a few
            questions from each CV, and the gates.
          </Typography>
          <Typography sx={{ fontFamily: FONT, fontSize: 12.5, color: C.faint, mt: 0.5 }}>
            That means two candidates are never asked the same thing, so their
            scores cannot be compared.
          </Typography>
        </Box>
      )}

      {questions.map((q, i) => (
        <Box
          key={i}
          sx={{ mb: 1, p: 1.5, borderRadius: 2, border: `1px solid ${C.line}`, bgcolor: C.card }}
        >
          <Stack direction="row" spacing={1.25} alignItems="flex-start">
            <Box
              sx={{
                width: 24,
                height: 32,
                flexShrink: 0,
                display: "grid",
                placeItems: "center",
                fontFamily: FONT,
                fontWeight: 800,
                fontSize: 12.5,
                color: C.faint,
              }}
            >
              {i + 1}
            </Box>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <TextField
                fullWidth
                size="small"
                multiline
                maxRows={4}
                disabled={qLocked}
                value={q.question}
                onChange={(e) =>
                  setQuestions((list) =>
                    list.map((x, n) => (n === i ? { ...x, question: e.target.value } : x)),
                  )
                }
                placeholder="Asked out loud, so keep it to one thing and plain spoken English."
                sx={field}
              />
              <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 0.75, flexWrap: "wrap" }}>
                <TextField
                  select
                  size="small"
                  disabled={qLocked}
                  value={q.competency_key || ""}
                  onChange={(e) =>
                    setQuestions((list) =>
                      list.map((x, n) =>
                        n === i ? { ...x, competency_key: e.target.value || null } : x,
                      ),
                    )
                  }
                  sx={{ ...field, minWidth: 210 }}
                >
                  <MenuItem value="" sx={{ fontFamily: FONT, fontSize: 12.5 }}>
                    not tied to a competency
                  </MenuItem>
                  {(rubric.competencies || []).map((c) => (
                    <MenuItem key={c.key} value={c.key} sx={{ fontFamily: FONT, fontSize: 12.5 }}>
                      {c.label} ({c.weight}%)
                    </MenuItem>
                  ))}
                </TextField>
                {q.why_this_question && (
                  <Typography
                    sx={{ fontFamily: FONT, fontSize: 12, color: C.faint, flex: 1, minWidth: 0 }}
                  >
                    {q.why_this_question}
                  </Typography>
                )}
              </Stack>
            </Box>
            <Tooltip title="Remove this question">
              <span>
                <IconButton
                  size="small"
                  disabled={qLocked}
                  onClick={() => setQuestions((list) => list.filter((_, n) => n !== i))}
                >
                  <DeleteOutlineRoundedIcon sx={{ fontSize: 18, color: C.faint }} />
                </IconButton>
              </span>
            </Tooltip>
          </Stack>
        </Box>
      ))}

      {questions.length < 9 && !qLocked && (
        <Button
          startIcon={<AddRoundedIcon sx={{ fontSize: 18 }} />}
          onClick={() =>
            setQuestions((list) => [...list, { question: "", competency_key: null, why_this_question: null }])
          }
          sx={{ textTransform: "none", fontWeight: 650, fontSize: 13, color: C.accent, mb: 3 }}
        >
          Add question
        </Button>
      )}

      {/* ── Hard gates ─────────────────────────────────────────────────── */}
      <Stack direction="row" alignItems="center" sx={{ mb: 0.5 }}>
        <Typography sx={{ fontFamily: FONT, fontWeight: 750, fontSize: 16, color: C.ink }}>
          Hard gates
        </Typography>
        <Typography sx={{ fontFamily: FONT, fontSize: 12.5, color: C.faint, ml: 1.5 }}>
          up to 4 — optional
        </Typography>
      </Stack>
      <Typography sx={{ fontFamily: FONT, fontSize: 12.5, color: C.muted, mb: 1.5, maxWidth: 780 }}>
        Disqualifications, not low scores. “Won’t relocate” folded into a weighted
        average lets a strong candidate average past a requirement they cannot
        meet. These are asked verbatim and decided in plain code, outside the
        model — and an unclear answer goes to a human rather than auto-rejecting.
      </Typography>

      {rubric.hard_gates.map((g, i) => {
        const op = gateOp(g);
        return (
          <Box
            key={i}
            sx={{ mb: 1.25, p: 1.75, borderRadius: 2, border: `1px solid ${C.line}`, bgcolor: C.card }}
          >
            <Stack direction={{ xs: "column", sm: "row" }} spacing={1.25}>
              <Box sx={{ flex: 2, minWidth: 0 }}>
                <Label hint="Asked to the candidate word for word, so write it as a question.">
                  Question
                </Label>
                <TextField
                  fullWidth
                  size="small"
                  value={g.question}
                  onChange={(e) => patchGate(i, { question: e.target.value })}
                  placeholder="How many days' notice do you have to serve?"
                  sx={field}
                />
              </Box>
              <Box sx={{ width: { xs: "100%", sm: 150 } }}>
                <Label>Key</Label>
                <TextField
                  fullWidth
                  size="small"
                  value={g.key}
                  onChange={(e) =>
                    patchGate(i, { key: e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, "_") })
                  }
                  placeholder="notice_period_days"
                  sx={field}
                />
              </Box>
              <Box sx={{ width: { xs: "100%", sm: 140 } }}>
                <Label>Rule</Label>
                <TextField
                  select
                  fullWidth
                  size="small"
                  value={op}
                  onChange={(e) => setGateOp(i, e.target.value)}
                  sx={field}
                >
                  {GATE_OPS.map((o) => (
                    <MenuItem key={o.key} value={o.key} sx={{ fontFamily: FONT, fontSize: 13 }}>
                      {o.label}
                    </MenuItem>
                  ))}
                </TextField>
              </Box>
              {op !== "required" && (
                <Box sx={{ width: { xs: "100%", sm: 100 } }}>
                  <Label>Value</Label>
                  <TextField
                    fullWidth
                    size="small"
                    value={g[op] ?? ""}
                    onChange={(e) => patchGate(i, { [op]: e.target.value.replace(/[^0-9.]/g, "") })}
                    inputProps={{ inputMode: "numeric" }}
                    sx={field}
                  />
                </Box>
              )}
              <Box sx={{ width: { xs: "100%", sm: 92 } }}>
                <Label hint="Set to days and “two months” is read as 60 rather than compared as 2.">
                  Unit
                </Label>
                <TextField
                  select
                  fullWidth
                  size="small"
                  value={g.unit || ""}
                  onChange={(e) => patchGate(i, { unit: e.target.value })}
                  sx={field}
                >
                  <MenuItem value="" sx={{ fontFamily: FONT, fontSize: 13 }}>
                    none
                  </MenuItem>
                  <MenuItem value="days" sx={{ fontFamily: FONT, fontSize: 13 }}>
                    days
                  </MenuItem>
                </TextField>
              </Box>
              <Box sx={{ display: "grid", placeItems: "center", pt: { sm: 2.5 } }}>
                <Tooltip title="Remove this gate">
                  <IconButton
                    size="small"
                    onClick={() =>
                      setRubric((r) => ({
                        ...r,
                        hard_gates: r.hard_gates.filter((_, n) => n !== i),
                      }))
                    }
                  >
                    <DeleteOutlineRoundedIcon sx={{ fontSize: 18, color: C.faint }} />
                  </IconButton>
                </Tooltip>
              </Box>
            </Stack>
          </Box>
        );
      })}

      {rubric.hard_gates.length < 4 && (
        <Button
          startIcon={<AddRoundedIcon sx={{ fontSize: 18 }} />}
          onClick={() => setRubric((r) => ({ ...r, hard_gates: [...r.hard_gates, blankGate()] }))}
          sx={{ textTransform: "none", fontWeight: 650, fontSize: 13, color: C.accent, mb: 3 }}
        >
          Add hard gate
        </Button>
      )}

      <Divider sx={{ my: 2, borderColor: C.hair }} />

      {/* ── Sizing ─────────────────────────────────────────────────────── */}
      <Typography sx={{ fontFamily: FONT, fontWeight: 750, fontSize: 16, color: C.ink, mb: 1.5 }}>
        Length
      </Typography>
      <Stack direction="row" spacing={1.5} sx={{ mb: 4 }}>
        <Box sx={{ width: 170 }}>
          <Label hint="Between 4 and 20, including the warm-up, the competency questions and the gates.">
            Total questions
          </Label>
          <TextField
            fullWidth
            size="small"
            value={rubric.total_questions}
            onChange={(e) =>
              setRubric((r) => ({ ...r, total_questions: e.target.value.replace(/[^0-9]/g, "") }))
            }
            inputProps={{ inputMode: "numeric" }}
            sx={field}
          />
        </Box>
        <Box sx={{ width: 170 }}>
          <Label hint="Between 5 and 120. Shown to the candidate in the invitation email.">
            Minutes
          </Label>
          <TextField
            fullWidth
            size="small"
            value={rubric.duration_minutes}
            onChange={(e) =>
              setRubric((r) => ({ ...r, duration_minutes: e.target.value.replace(/[^0-9]/g, "") }))
            }
            inputProps={{ inputMode: "numeric" }}
            sx={field}
          />
        </Box>
      </Stack>

      <Snackbar
        open={Boolean(toast)}
        autoHideDuration={7000}
        onClose={() => setToast(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          severity={toast?.severity || "info"}
          onClose={() => setToast(null)}
          sx={{ maxWidth: 620, fontFamily: FONT, borderRadius: 2, fontSize: 13.5 }}
        >
          {toast?.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default RubricEditor;
